// src/components/annotation/utils/drawing.ts

import { AnnotationDataBoundingBox } from '../../../types/models/annotation'
import { Tag } from '../../../types/models/tag'
import { MousePosition } from '../hooks/useImageAnnotation'
import { getContrastYIQ } from '../../../utils'
import { pointsArrayToStructured, isAxisAligned, getMinMaxFromPoints } from '../../../utils/helpers'

// ============================================================================
// CONTRAST CACHING - Performance optimization
// ============================================================================

interface ContrastCache {
  imageData: ImageData | null
  imageUrl: string
  contrast: number
  canvasWidth: number
  canvasHeight: number
}

let contrastCache: ContrastCache = {
  imageData: null,
  imageUrl: '',
  contrast: 0,
  canvasWidth: 0,
  canvasHeight: 0,
}

/**
 * Clear contrast cache when image changes
 */
export const clearContrastCache = (): void => {
  contrastCache = {
    imageData: null,
    imageUrl: '',
    contrast: 0,
    canvasWidth: 0,
    canvasHeight: 0,
  }
}

// ============================================================================
// ORIENTED BOUNDING BOX DRAWING UTILITIES
// ============================================================================

/**
 * Draw an oriented bounding box using strokePath for full rotation support
 * Points are expected to be in pixel coordinates
 */
export const drawBoundingBoxFromPoints = (
  ctx: CanvasRenderingContext2D,
  points: number[],
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  strokeStyle: string,
  fillStyle?: string,
  lineWidth = 2
) => {
  if (points.length !== 8) return

  // Convert pixel coordinates to canvas coordinates
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight

  const scaledPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  ctx.beginPath()
  ctx.moveTo(scaledPoints[0], scaledPoints[1]) // top-left
  ctx.lineTo(scaledPoints[2], scaledPoints[3]) // top-right
  ctx.lineTo(scaledPoints[4], scaledPoints[5]) // bottom-right
  ctx.lineTo(scaledPoints[6], scaledPoints[7]) // bottom-left
  ctx.closePath()

  // Fill if fillStyle provided
  if (fillStyle) {
    ctx.fillStyle = fillStyle
    ctx.fill()
  }

  // Stroke
  ctx.strokeStyle = strokeStyle
  ctx.lineWidth = lineWidth
  ctx.stroke()
}

/**
 * Draw resize handles for oriented bounding box
 * Points are expected to be in pixel coordinates
 */
export const drawOrientedResizeHandles = (
  ctx: CanvasRenderingContext2D,
  points: number[],
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  handleColor: string,
  handleSize: number,
  zoomLevel: number,
  cssScale = 1
) => {
  if (points.length !== 8) return

  // Convert pixel coordinates to canvas coordinates
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight

  const canvasPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  const bbox = pointsArrayToStructured(canvasPoints)

  ctx.strokeStyle = handleColor
  ctx.fillStyle = handleColor
  // Keep thickness consistent with bbox outline and zoom
  // const borderWidth = Math.max(1, 2 / zoomLevel)
  const borderWidth = Math.max(0.25, 2 / (zoomLevel * cssScale))
  // Stroke scales with zoom; keep thin at high zoom
  // Match border thickness for handle strokes
  const handleLineWidth = borderWidth
  const haloLineWidth = Math.max(0.3, handleLineWidth + 1 / (zoomLevel * cssScale)) // subtle halo
  const haloColor = getContrastYIQ(handleColor) === 'white' ? '#ffffff' : '#000000'
  ctx.lineWidth = handleLineWidth
  ctx.lineCap = 'round'

  // Corner handles as 90-degree arcs inside the box
  // Use the provided handleSize directly so OBB matches BBox scaling behavior
  const radius = Math.max(0.5, handleSize)
  // Consistent insets
  const edgeInset = borderWidth + handleLineWidth * 0.25
  const centerOffset = radius + borderWidth + handleLineWidth * 0.25
  // Corner arcs aligned to OBB orientation using local axes
  const cornerU = {
    x: bbox.topRight.x - bbox.topLeft.x,
    y: bbox.topRight.y - bbox.topLeft.y,
  }
  const cornerV = {
    x: bbox.bottomLeft.x - bbox.topLeft.x,
    y: bbox.bottomLeft.y - bbox.topLeft.y,
  }
  const ulenLocal = Math.hypot(cornerU.x, cornerU.y) || 1
  const vlenLocal = Math.hypot(cornerV.x, cornerV.y) || 1
  const ux = { x: cornerU.x / ulenLocal, y: cornerU.y / ulenLocal }
  const vy = { x: cornerV.x / vlenLocal, y: cornerV.y / vlenLocal }

  const drawCornerArcOriented = (
    corner: { x: number; y: number },
    xAxis: { x: number; y: number },
    yAxis: { x: number; y: number },
    start: number,
    end: number
  ) => {
    ctx.save()
    // Move origin to corner and rotate axes to local orientation
    ctx.translate(corner.x, corner.y)
    ctx.transform(xAxis.x, xAxis.y, yAxis.x, yAxis.y, 0, 0)

    // UX: Add subtle shadow for depth (like Labelbox)
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    ctx.shadowBlur = 2 / (zoomLevel * cssScale)
    ctx.shadowOffsetX = 0.5 / (zoomLevel * cssScale)
    ctx.shadowOffsetY = 0.5 / (zoomLevel * cssScale)

    // Halo
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.arc(centerOffset, centerOffset, radius, start, end)
    ctx.stroke()

    // Foreground (disable shadow for foreground to keep it crisp)
    ctx.shadowColor = 'transparent'
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.arc(centerOffset, centerOffset, radius, start, end)
    ctx.stroke()
    ctx.restore()
  }
  // Orient axes so +x and +y point inward; then always draw 180->270 in local space
  drawCornerArcOriented(bbox.topLeft, ux, vy, Math.PI, 1.5 * Math.PI)
  drawCornerArcOriented(bbox.topRight, { x: -ux.x, y: -ux.y }, vy, Math.PI, 1.5 * Math.PI)
  drawCornerArcOriented(
    bbox.bottomRight,
    { x: -ux.x, y: -ux.y },
    { x: -vy.x, y: -vy.y },
    Math.PI,
    1.5 * Math.PI
  )
  drawCornerArcOriented(bbox.bottomLeft, ux, { x: -vy.x, y: -vy.y }, Math.PI, 1.5 * Math.PI)

  // Edge handles as centered segments inside the bbox: exactly 20% of the edge length
  const drawEdgeSegment = (ax: number, ay: number, bx: number, by: number) => {
    const dx = bx - ax
    const dy = by - ay
    const length = Math.hypot(dx, dy)
    if (length === 0) return
    const segLength = length * 0.2
    const mx = (ax + bx) / 2
    const my = (ay + by) / 2
    const ux = dx / length
    const uy = dy / length
    const hx = (segLength / 2) * ux
    const hy = (segLength / 2) * uy
    // Inset the segment slightly inside the box along the inward normal
    const centerX = (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4
    const centerY = (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4
    // Normal vector (rotate tangent by +90deg)
    let nx = -uy
    let ny = ux
    // Choose inward direction by dot with center vector
    const dot = (centerX - mx) * nx + (centerY - my) * ny
    if (dot < 0) {
      nx = -nx
      ny = -ny
    }
    const offX = nx * edgeInset
    const offY = ny * edgeInset

    const x1 = mx - hx + offX
    const y1 = my - hy + offY
    const x2 = mx + hx + offX
    const y2 = my + hy + offY

    // Halo underlay
    ctx.save()

    // UX: Add subtle shadow for depth
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    ctx.shadowBlur = 2 / (zoomLevel * cssScale)
    ctx.shadowOffsetX = 0.5 / (zoomLevel * cssScale)
    ctx.shadowOffsetY = 0.5 / (zoomLevel * cssScale)

    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()

    // Foreground stroke (disable shadow for crisp foreground)
    ctx.shadowColor = 'transparent'
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
    ctx.restore()
  }

  drawEdgeSegment(bbox.topLeft.x, bbox.topLeft.y, bbox.topRight.x, bbox.topRight.y)
  drawEdgeSegment(bbox.topRight.x, bbox.topRight.y, bbox.bottomRight.x, bbox.bottomRight.y)
  drawEdgeSegment(bbox.bottomRight.x, bbox.bottomRight.y, bbox.bottomLeft.x, bbox.bottomLeft.y)
  drawEdgeSegment(bbox.bottomLeft.x, bbox.bottomLeft.y, bbox.topLeft.x, bbox.topLeft.y)

  // Rotate handle: small circle offset from top edge inward along its normal
  {
    const ax = bbox.topLeft.x
    const ay = bbox.topLeft.y
    const bx = bbox.topRight.x
    const by = bbox.topRight.y
    const mx = (ax + bx) / 2
    const my = (ay + by) / 2
    const dx = bx - ax
    const dy = by - ay
    const len = Math.hypot(dx, dy) || 1
    let nx = -dy / len
    let ny = dx / len
    const cx = (bbox.topLeft.x + bbox.topRight.x + bbox.bottomRight.x + bbox.bottomLeft.x) / 4
    const cy = (bbox.topLeft.y + bbox.topRight.y + bbox.bottomRight.y + bbox.bottomLeft.y) / 4
    const dot = (mx - cx) * nx + (my - cy) * ny
    // Choose inward direction toward the center
    if (dot > 0) {
      nx = -nx
      ny = -ny
    }
    const rotateOffset = radius + edgeInset
    const rx = mx + nx * rotateOffset
    const ry = my + ny * rotateOffset
    const rotateRadius = Math.max(0.5, handleSize * 0.8)

    // UX: Add subtle shadow for depth
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    ctx.shadowBlur = 2 / (zoomLevel * cssScale)
    ctx.shadowOffsetX = 0.5 / (zoomLevel * cssScale)
    ctx.shadowOffsetY = 0.5 / (zoomLevel * cssScale)

    // Halo
    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.arc(rx, ry, rotateRadius, 0, Math.PI * 2)
    ctx.stroke()

    // Foreground (disable shadow for crisp foreground)
    ctx.shadowColor = 'transparent'
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.arc(rx, ry, rotateRadius, 0, Math.PI * 2)
    ctx.stroke()

    // Subtle tick line from edge midpoint to rotate handle
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)'
    ctx.shadowBlur = 2 / (zoomLevel * cssScale)
    ctx.shadowOffsetX = 0.5 / (zoomLevel * cssScale)
    ctx.shadowOffsetY = 0.5 / (zoomLevel * cssScale)

    ctx.beginPath()
    ctx.strokeStyle = haloColor
    ctx.lineWidth = haloLineWidth
    ctx.moveTo(mx, my)
    ctx.lineTo(rx, ry)
    ctx.stroke()

    ctx.shadowColor = 'transparent'
    ctx.beginPath()
    ctx.strokeStyle = handleColor
    ctx.lineWidth = handleLineWidth
    ctx.moveTo(mx, my)
    ctx.lineTo(rx, ry)
    ctx.stroke()
  }

  // Start-corner border highlight: recolor a short L-shape of the actual border at top-left
  const highlightLen = Math.max(1, 1 / zoomLevel)
  const highlightColor = getContrastYIQ(handleColor)
  const start = bbox.topLeft
  ctx.save()
  ctx.strokeStyle = highlightColor
  ctx.lineWidth = (borderWidth * 2) / zoomLevel
  ctx.lineCap = 'round'
  // Along top edge direction (ux)
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(start.x + ux.x * highlightLen, start.y + ux.y * highlightLen)
  ctx.stroke()
  // Along left edge direction (vy)
  ctx.beginPath()
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(start.x + vy.x * highlightLen, start.y + vy.y * highlightLen)
  ctx.stroke()
  ctx.restore()
}

/**
 * Check if a point is near any of the bounding box handles
 * Points and mouse coordinates are in pixel coordinates
 * handleSizeCanvas is the handle size in canvas coordinates
 */
export const getOrientedResizeHandle = (
  points: number[],
  mouseX: number,
  mouseY: number,
  handleSizeCanvas: number,
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number
): string | null => {
  if (points.length !== 8) return null

  // Convert pixel coordinates to canvas coordinates for consistent comparison
  const scaleX = canvasWidth / imageWidth
  const scaleY = canvasHeight / imageHeight
  const canvasPoints = [
    points[0] * scaleX, // top-left x
    points[1] * scaleY, // top-left y
    points[2] * scaleX, // top-right x
    points[3] * scaleY, // top-right y
    points[4] * scaleX, // bottom-right x
    points[5] * scaleY, // bottom-right y
    points[6] * scaleX, // bottom-left x
    points[7] * scaleY, // bottom-left y
  ]

  // Convert mouse coordinates to canvas space
  const canvasMouseX = mouseX * scaleX
  const canvasMouseY = mouseY * scaleY

  const bbox = pointsArrayToStructured(canvasPoints)
  const handles = [
    { name: 'topLeft', point: bbox.topLeft },
    { name: 'topRight', point: bbox.topRight },
    { name: 'bottomRight', point: bbox.bottomRight },
    { name: 'bottomLeft', point: bbox.bottomLeft },
  ]

  for (const handle of handles) {
    let x = handle.point.x
    let y = handle.point.y

    // Adjust handle position to match the drawing logic (handles are inside the box)
    switch (handle.name) {
      case 'topLeft':
        x += handleSizeCanvas / 2
        y += handleSizeCanvas / 2
        break
      case 'topRight':
        x -= handleSizeCanvas / 2
        y += handleSizeCanvas / 2
        break
      case 'bottomRight':
        x -= handleSizeCanvas / 2
        y -= handleSizeCanvas / 2
        break
      case 'bottomLeft':
        x += handleSizeCanvas / 2
        y -= handleSizeCanvas / 2
        break
    }

    const rect = {
      left: x - handleSizeCanvas / 2,
      right: x + handleSizeCanvas / 2,
      top: y - handleSizeCanvas / 2,
      bottom: y + handleSizeCanvas / 2,
    }

    // Check if mouse is within the handle area (all in canvas coordinates)
    if (
      canvasMouseX >= rect.left &&
      canvasMouseX <= rect.right &&
      canvasMouseY >= rect.top &&
      canvasMouseY <= rect.bottom
    ) {
      return handle.name
    }
  }
  return null
}

/**
 * Hybrid drawing function that uses optimized rect drawing for axis-aligned boxes
 * and path drawing for oriented boxes
 * Points are expected to be in pixel coordinates
 */
export const drawBoundingBoxOptimized = (
  ctx: CanvasRenderingContext2D,
  box: AnnotationDataBoundingBox,
  imageWidth: number,
  imageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
  strokeStyle: string,
  fillStyle?: string,
  lineWidth = 2,
  contentWidth?: number,
  contentHeight?: number
) => {
  // Use points if available and valid
  // After API transform, points are in pixel coordinates (using content.width/content.height from API)
  // We need to convert them to natural image pixels (using imageWidth/imageHeight)
  if (box.points && box.points.length === 8) {
    // Check if normalized
    const maxX = Math.max(box.points[0], box.points[2], box.points[4], box.points[6])
    const maxY = Math.max(box.points[1], box.points[3], box.points[5], box.points[7])
    const areNormalized = maxX <= 1 && maxY <= 1 && (maxX > 0 || maxY > 0)

    let scaledPoints: number[]
    if (areNormalized) {
      // Normalized [0,1] relative to contentWidth/contentHeight → convert to natural pixels
      // This shouldn't happen after API transform, but handle it for safety
      scaledPoints = [
        box.points[0] * imageWidth,
        box.points[1] * imageHeight,
        box.points[2] * imageWidth,
        box.points[3] * imageHeight,
        box.points[4] * imageWidth,
        box.points[5] * imageHeight,
        box.points[6] * imageWidth,
        box.points[7] * imageHeight,
      ]
    } else {
      // Points are in pixel coordinates from API (using content.width/content.height)
      // ALWAYS scale from content dimensions to natural dimensions if they differ
      // This matches what the gallery view does!

      const needsScaling =
        contentWidth &&
        contentHeight &&
        (contentWidth !== imageWidth || contentHeight !== imageHeight)

      if (needsScaling && contentWidth && contentHeight) {
        // Scale from content space to natural space (like gallery view does)
        const scaleX = imageWidth / contentWidth
        const scaleY = imageHeight / contentHeight

        scaledPoints = box.points.map((coord, idx) => {
          return idx % 2 === 0 ? coord * scaleX : coord * scaleY
        })
      } else {
        // Content dimensions match natural dimensions - use as-is
        scaledPoints = [...box.points]
      }
    }

    // For axis-aligned boxes, use the faster rect drawing
    if (isAxisAligned(scaledPoints)) {
      const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(scaledPoints)

      const scaleX = canvasWidth / imageWidth
      const scaleY = canvasHeight / imageHeight

      const x = xmin * scaleX
      const y = ymin * scaleY
      const width = (xmax - xmin) * scaleX
      const height = (ymax - ymin) * scaleY

      // Fill if fillStyle provided
      if (fillStyle) {
        ctx.fillStyle = fillStyle
        ctx.fillRect(x, y, width, height)
      }

      // Stroke
      ctx.strokeStyle = strokeStyle
      ctx.lineWidth = lineWidth
      ctx.strokeRect(x, y, width, height)
    } else {
      // Use path drawing for oriented boxes (points are in pixels)
      // Scale coordinates if needed before passing to drawing function
      drawBoundingBoxFromPoints(
        ctx,
        scaledPoints,
        imageWidth,
        imageHeight,
        canvasWidth,
        canvasHeight,
        strokeStyle,
        fillStyle,
        lineWidth
      )
    }
  } else {
    // Fallback to min/max coordinates for legacy boxes
    // Check if they're normalized [0,1] relative to contentWidth/contentHeight
    const xmaxRaw = box.xmax ?? 0
    const ymaxRaw = box.ymax ?? 0
    const areNormalized = xmaxRaw <= 1 && ymaxRaw <= 1 && (xmaxRaw > 0 || ymaxRaw > 0)

    let scaledXmin: number, scaledYmin: number, scaledXmax: number, scaledYmax: number

    if (areNormalized) {
      // Normalized [0,1] relative to contentWidth/contentHeight → convert to natural pixels
      scaledXmin = (box.xmin ?? 0) * imageWidth
      scaledYmin = (box.ymin ?? 0) * imageHeight
      scaledXmax = xmaxRaw * imageWidth
      scaledYmax = ymaxRaw * imageHeight
    } else {
      // Coordinates are in content pixel space - scale to natural space if needed
      const needsScaling =
        contentWidth &&
        contentHeight &&
        (contentWidth !== imageWidth || contentHeight !== imageHeight)

      if (needsScaling && contentWidth && contentHeight) {
        // Scale from content space to natural space (like gallery view does)
        const scaleX = imageWidth / contentWidth
        const scaleY = imageHeight / contentHeight

        scaledXmin = (box.xmin ?? 0) * scaleX
        scaledYmin = (box.ymin ?? 0) * scaleY
        scaledXmax = xmaxRaw * scaleX
        scaledYmax = ymaxRaw * scaleY
      } else {
        // Content dimensions match natural dimensions - use as-is
        scaledXmin = box.xmin ?? 0
        scaledYmin = box.ymin ?? 0
        scaledXmax = xmaxRaw
        scaledYmax = ymaxRaw
      }
    }

    const scaleX = canvasWidth / imageWidth
    const scaleY = canvasHeight / imageHeight

    const x = scaledXmin * scaleX
    const y = scaledYmin * scaleY
    const width = (scaledXmax - scaledXmin) * scaleX
    const height = (scaledYmax - scaledYmin) * scaleY

    // Fill if fillStyle provided
    if (fillStyle) {
      ctx.fillStyle = fillStyle
      ctx.fillRect(x, y, width, height)
    }

    // Stroke
    ctx.strokeStyle = strokeStyle
    ctx.lineWidth = lineWidth
    ctx.strokeRect(x, y, width, height)
  }
}

const PADDING_FACTOR = 0.3 // 10% padding around the bbox when zooming

export const calculateZoomForBBox = (
  bbox: AnnotationDataBoundingBox,
  canvas: HTMLCanvasElement,
  bBoxScaleX: number,
  bBoxScaleY: number,
  imageWidth?: number,
  imageHeight?: number,
  contentWidth?: number,
  contentHeight?: number
): { zoomLevel: number; panOffset: MousePosition } => {
  // Use contain-fit mapping so centering works with letterboxing
  const iw = imageWidth && imageHeight ? imageWidth : 1
  const ih = imageHeight && imageWidth ? imageHeight : 1
  const fitScale = imageWidth && imageHeight ? Math.min(canvas.width / iw, canvas.height / ih) : 1
  const drawW = imageWidth && imageHeight ? iw * fitScale : canvas.width
  const drawH = imageWidth && imageHeight ? ih * fitScale : canvas.height
  const offsetX = (canvas.width - drawW) / 2
  const offsetY = (canvas.height - drawH) / 2

  // Determine bbox center and size in image pixels
  let bboxCenterX: number, bboxCenterY: number, bboxWidth: number, bboxHeight: number
  if (bbox.points && bbox.points.length === 8 && imageWidth && imageHeight) {
    // Check if points are normalized [0,1] relative to contentWidth/contentHeight
    const maxX = Math.max(bbox.points[0], bbox.points[2], bbox.points[4], bbox.points[6])
    const maxY = Math.max(bbox.points[1], bbox.points[3], bbox.points[5], bbox.points[7])
    const areNormalized = maxX <= 1 && maxY <= 1 && (maxX > 0 || maxY > 0)

    let scaledPoints: number[]
    if (areNormalized) {
      // Normalized [0,1] relative to contentWidth/contentHeight → convert to natural pixels
      scaledPoints = [
        bbox.points[0] * imageWidth,
        bbox.points[1] * imageHeight,
        bbox.points[2] * imageWidth,
        bbox.points[3] * imageHeight,
        bbox.points[4] * imageWidth,
        bbox.points[5] * imageHeight,
        bbox.points[6] * imageWidth,
        bbox.points[7] * imageHeight,
      ]
    } else {
      // Already in pixel coordinates - scale from content dimensions to natural dimensions if needed
      let coordinateScaleX = 1
      let coordinateScaleY = 1
      if (contentWidth && contentHeight) {
        if (contentWidth !== imageWidth || contentHeight !== imageHeight) {
          coordinateScaleX = imageWidth / contentWidth
          coordinateScaleY = imageHeight / contentHeight
        }
      }
      scaledPoints = bbox.points.map((coord, idx) => {
        return idx % 2 === 0 ? coord * coordinateScaleX : coord * coordinateScaleY
      })
    }

    const { xmin, ymin, xmax, ymax } = getMinMaxFromPoints(scaledPoints)
    const centerXImg = (xmin + xmax) / 2
    const centerYImg = (ymin + ymax) / 2
    bboxWidth = (xmax - xmin) * (drawW / iw)
    bboxHeight = (ymax - ymin) * (drawH / ih)
    bboxCenterX = offsetX + centerXImg * (drawW / iw)
    bboxCenterY = offsetY + centerYImg * (drawH / ih)
  } else {
    // Check if min/max are normalized [0,1] relative to contentWidth/contentHeight
    const xmaxRaw = bbox.xmax ?? 0
    const ymaxRaw = bbox.ymax ?? 0
    const areNormalized = xmaxRaw <= 1 && ymaxRaw <= 1 && (xmaxRaw > 0 || ymaxRaw > 0)

    let xmin: number, xmax: number, ymin: number, ymax: number

    if (areNormalized && imageWidth && imageHeight) {
      // Normalized [0,1] relative to contentWidth/contentHeight → convert to natural pixels
      xmin = (bbox.xmin ?? 0) * imageWidth
      ymin = (bbox.ymin ?? 0) * imageHeight
      xmax = xmaxRaw * imageWidth
      ymax = ymaxRaw * imageHeight
    } else {
      // Already in pixel coordinates - scale from content dimensions to natural dimensions if needed
      let coordinateScaleX = 1
      let coordinateScaleY = 1
      if (contentWidth && contentHeight && imageWidth && imageHeight) {
        if (contentWidth !== imageWidth || contentHeight !== imageHeight) {
          coordinateScaleX = imageWidth / contentWidth
          coordinateScaleY = imageHeight / contentHeight
        }
      }
      xmin = (bbox.xmin ?? 0) * coordinateScaleX
      xmax = xmaxRaw * coordinateScaleX
      ymin = (bbox.ymin ?? 0) * coordinateScaleY
      ymax = ymaxRaw * coordinateScaleY
    }

    const centerXImg = (xmin + xmax) / 2
    const centerYImg = (ymin + ymax) / 2
    bboxWidth = (xmax - xmin) * (drawW / iw)
    bboxHeight = (ymax - ymin) * (drawH / ih)
    bboxCenterX = offsetX + centerXImg * (drawW / iw)
    bboxCenterY = offsetY + centerYImg * (drawH / ih)
  }

  // Calculate zoom levels needed to fit bbox in both dimensions
  const horizontalZoom = canvas.width / (bboxWidth * (1 + 2 * PADDING_FACTOR))
  const verticalZoom = canvas.height / (bboxHeight * (1 + 2 * PADDING_FACTOR))

  // Use the smaller zoom level to ensure the entire bbox is visible
  const zoomLevel = Math.min(horizontalZoom, verticalZoom) / 2.5

  // Calculate required pan offset to center the bbox
  const panOffset = {
    x: canvas.width / 2 - bboxCenterX,
    y: canvas.height / 2 - bboxCenterY,
  }

  return { zoomLevel, panOffset }
}

/**
 * Draw status icon overlay on a bounding box (eye, checkmark, or X)
 * @param ctx Canvas rendering context
 * @param centerX Center X position in canvas coordinates
 * @param centerY Center Y position in canvas coordinates
 * @param status Box status ('untouched', 'accepted', or 'rejected')
 * @param size Icon size in canvas pixels
 * @param zoomLevel Current zoom level
 * @param cssScale CSS scaling factor
 */
const drawStatusIcon = (
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  status: 'untouched' | 'accepted' | 'rejected',
  size: number
) => {
  ctx.save()

  // Size is already calculated relative to box, just use it directly
  // This makes the icon scale WITH the bounding box
  const radius = size / 2

  // Draw semi-transparent background circle
  ctx.beginPath()
  ctx.arc(centerX, centerY, radius * 1.1, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
  ctx.fill()

  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = radius * 0.1
  ctx.stroke()

  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (status === 'accepted') {
    // Draw green checkmark
    ctx.strokeStyle = '#00ff00'
    ctx.lineWidth = radius * 0.15
    ctx.beginPath()
    ctx.moveTo(centerX - radius * 0.5, centerY)
    ctx.lineTo(centerX - radius * 0.1, centerY + radius * 0.4)
    ctx.lineTo(centerX + radius * 0.6, centerY - radius * 0.5)
    ctx.stroke()
  } else if (status === 'rejected') {
    // Draw red X
    ctx.strokeStyle = '#ff0000'
    ctx.lineWidth = radius * 0.15
    ctx.beginPath()
    ctx.moveTo(centerX - radius * 0.5, centerY - radius * 0.5)
    ctx.lineTo(centerX + radius * 0.5, centerY + radius * 0.5)
    ctx.moveTo(centerX + radius * 0.5, centerY - radius * 0.5)
    ctx.lineTo(centerX - radius * 0.5, centerY + radius * 0.5)
    ctx.stroke()
  } else if (status === 'untouched') {
    // Draw eye icon
    ctx.strokeStyle = '#ffa500' // Orange
    ctx.lineWidth = radius * 0.12
    // Eye outline (ellipse)
    ctx.beginPath()
    ctx.ellipse(centerX, centerY, radius * 0.65, radius * 0.35, 0, 0, Math.PI * 2)
    ctx.stroke()
    // Pupil
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius * 0.2, 0, Math.PI * 2)
    ctx.fillStyle = '#ffa500'
    ctx.fill()
  }

  ctx.restore()
}

export const drawAnnotations = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  zoomLevel: number,
  panOffset: MousePosition,
  filteredBoxes: AnnotationDataBoundingBox[],
  showConfidence: boolean,
  tags: Tag[],
  useBBoxes: boolean,
  showBBoxes: boolean,
  newBoundingBox: AnnotationDataBoundingBox | null,
  selectedBBox: AnnotationDataBoundingBox | null,
  mousePosition: MousePosition,
  contrast: number,
  bBoxIndex?: number,
  hoveredBoxIndex?: number,
  isMouseOverCanvas = false,
  creationAxisStart: { x: number; y: number } | null = null,
  creationPhase: 'idle' | 'axisStart' | 'axisFixed' = 'idle',
  isEditable = true,
  labelMode: 'class' | 'class+confidence' = 'class+confidence',
  bboxStatuses?: Record<number, 'untouched' | 'accepted' | 'rejected'>,
  contentWidth?: number,
  contentHeight?: number
) => {
  // Prevent unnecessary redraws during resize
  if (ctx.canvas !== canvas) return

  // Clear the entire canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height)

  // Save the context state
  ctx.save()

  // UX: High-quality image rendering for professional look (like Labelbox)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  // UX: Crisp, smooth line rendering for annotations
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // Apply transformations only if we're in a bounding box project or if we're zooming/panning
  // if (useBBoxes || zoomLevel !== 0.85 || panOffset.x !== 0 || panOffset.y !== 0) {
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.scale(zoomLevel, zoomLevel)
  ctx.translate(-canvas.width / 2, -canvas.height / 2)
  ctx.translate(panOffset.x, panOffset.y)
  // }

  // Determine CSS scaling (canvas element may be scaled by layout)
  const rect = canvas.getBoundingClientRect()
  const cssScaleX = rect.width && canvas.width ? rect.width / canvas.width : 1
  const cssScaleY = rect.height && canvas.height ? rect.height / canvas.height : 1
  const cssScale = Math.max(cssScaleX, cssScaleY) || 1

  // Contain-fit the image inside the canvas with letterboxing offsets
  // CRITICAL FIX: Always use natural dimensions (displayed image) for coordinate system
  // The bounding box coordinates are relative to the displayed image, not original metadata
  const imgW = image.naturalWidth || 1
  const imgH = image.naturalHeight || 1
  const fitScale = Math.min(canvas.width / imgW, canvas.height / imgH)
  const drawW = imgW * fitScale
  const drawH = imgH * fitScale
  const offsetX = (canvas.width - drawW) / 2
  const offsetY = (canvas.height - drawH) / 2

  // Draw image using contain fit
  ctx.drawImage(image, offsetX, offsetY, drawW, drawH)

  // Apply contrast with caching for performance
  // This is the #1 performance bottleneck - can process millions of pixels per frame
  if (contrast && contrast !== 0) {
    const cacheKey = image.src
    const canUseCache =
      contrastCache.imageData &&
      contrastCache.imageUrl === cacheKey &&
      contrastCache.contrast === contrast &&
      contrastCache.canvasWidth === canvas.width &&
      contrastCache.canvasHeight === canvas.height

    if (canUseCache && contrastCache.imageData) {
      // Use cached contrast-adjusted image data (fast path)
      ctx.putImageData(contrastCache.imageData, 0, 0)
    } else {
      // Calculate contrast and cache result (slow path, only runs when needed)
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const factor = (259 * (contrast + 255)) / (255 * (259 - contrast))
      const data = imageData.data
      for (let i = 0; i < data.length; i += 4) {
        data[i] = factor * (data[i] - 128) + 128 // Red
        data[i + 1] = factor * (data[i + 1] - 128) + 128 // Green
        data[i + 2] = factor * (data[i + 2] - 128) + 128 // Blue
      }
      ctx.putImageData(imageData, 0, 0)

      // Cache the result
      contrastCache = {
        imageData: imageData,
        imageUrl: cacheKey,
        contrast: contrast,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
      }
    }
  }

  // Spotlight effect removed - no darkening overlay on hover

  if (showBBoxes) {
    const bBoxScaleX = drawW / imgW
    const bBoxScaleY = drawH / imgH

    // Draw existing bounding boxes
    const selectedBBoxIndex = filteredBoxes.findIndex((b) => {
      return b.class_index === selectedBBox?.class_index
    })
    // Translate drawing into the image draw area so (0,0) maps to image origin
    ctx.save()
    ctx.translate(offsetX, offsetY)

    // PERFORMANCE: Create a combined list that prefers selectedBBox over its Redux version
    // This allows canvas to show real-time updates without Redux updates (no snippet re-renders!)
    const boxesToDraw = filteredBoxes.map((box) => {
      // If this box is selected and we have a selectedBBox, use selectedBBox instead
      // This gives real-time visual feedback without Redux updates
      if (selectedBBox && box.class_index === selectedBBox.class_index) {
        return selectedBBox
      }
      return box
    })

    boxesToDraw.forEach((box, index) => {
      // Draw all boxes, not just the selected one
      // This was causing all other boxes to disappear when one was selected
      if (
        bBoxIndex === undefined ||
        (index === bBoxIndex && selectedBBox?.class_index === box.class_index)
      ) {
        const selectedTag = tags?.find((tag) => tag.id === box.name)

        const color = `#${selectedTag?.color.toString(16).padStart(6, '0').slice(0, 6) || '#80085'}`
        if (selectedTag) {
          // Use the new optimized drawing function that supports both axis-aligned and oriented boxes
          drawBoundingBoxOptimized(
            ctx,
            box,
            imgW,
            imgH,
            drawW,
            drawH,
            color,
            undefined, // No fill for basic outline
            2 / (zoomLevel * cssScale),
            contentWidth, // Pass actual content dimensions from API
            contentHeight // Pass actual content dimensions from API
          )

          // Highlight selected or hovered box
          if (
            (selectedBBox && selectedBBoxIndex === index) ||
            box.class_index === hoveredBoxIndex
          ) {
            const fillColor = `#${selectedTag.color.toString(16).padStart(6, '0').slice(0, 6)}44`

            // Draw fill highlight using the optimized function
            drawBoundingBoxOptimized(
              ctx,
              box,
              imgW,
              imgH,
              drawW,
              drawH,
              color,
              fillColor,
              2 / (zoomLevel * cssScale),
              contentWidth, // Pass actual content dimensions from API
              contentHeight // Pass actual content dimensions from API
            )

            // Draw resize handles only when editable and for selected box
            if (isEditable && selectedBBox && selectedBBoxIndex === index) {
              // Convert box to natural space for handle drawing
              // Handles need to be in the same coordinate system as the drawn box
              const needsScaling =
                contentWidth && contentHeight && (contentWidth !== imgW || contentHeight !== imgH)

              let handleBox = box
              if (needsScaling && contentWidth && contentHeight) {
                const scaleX = imgW / contentWidth
                const scaleY = imgH / contentHeight

                if (box.points && box.points.length === 8) {
                  handleBox = {
                    ...box,
                    points: box.points.map((coord, idx) =>
                      idx % 2 === 0 ? coord * scaleX : coord * scaleY
                    ),
                    xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
                    ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
                    xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
                    ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
                  }
                } else {
                  handleBox = {
                    ...box,
                    xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
                    ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
                    xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
                    ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
                  }
                }
              }

              // Base handle size on the box's canvas size so squares/rectangles scale naturally
              const pts =
                handleBox.points && handleBox.points.length === 8
                  ? handleBox.points
                  : [
                      handleBox.xmin ?? 0,
                      handleBox.ymin ?? 0,
                      handleBox.xmax ?? 0,
                      handleBox.ymin ?? 0,
                      handleBox.xmax ?? 0,
                      handleBox.ymax ?? 0,
                      handleBox.xmin ?? 0,
                      handleBox.ymax ?? 0,
                    ]
              const xs = [pts[0], pts[2], pts[4], pts[6]]
              const ys = [pts[1], pts[3], pts[5], pts[7]]
              const minX = Math.min(...xs)
              const maxX = Math.max(...xs)
              const minY = Math.min(...ys)
              const maxY = Math.max(...ys)
              const widthCanvas = (maxX - minX) * (canvas.width / image.naturalWidth)
              const heightCanvas = (maxY - minY) * (canvas.height / image.naturalHeight)
              const minSideCanvas = Math.max(0.5, Math.min(widthCanvas, heightCanvas))
              const HANDLE_RATIO = 0.06 // 6% of shortest side
              const handleSize = minSideCanvas * HANDLE_RATIO

              // Always draw unified handle style; derive points if missing
              const handlePts = pts
              drawOrientedResizeHandles(
                ctx,
                handlePts,
                imgW,
                imgH,
                drawW,
                drawH,
                // Use the same color as the bbox outline for consistency
                color,
                handleSize,
                zoomLevel,
                cssScale
              )
            }
          }
        }

        if (showConfidence && selectedBBox && selectedBBoxIndex === index) {
          // Convert box to natural space for label positioning
          const needsScaling =
            contentWidth && contentHeight && (contentWidth !== imgW || contentHeight !== imgH)

          let labelBox = box
          if (needsScaling && contentWidth && contentHeight) {
            const scaleX = imgW / contentWidth
            const scaleY = imgH / contentHeight
            labelBox = {
              ...box,
              xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
              ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
              xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
              ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
            }
          }

          // Draw confidence percentage and, if selected, append class name
          const confidence = box.confidence || 0
          const confidenceText = `${(confidence * 100).toFixed(1)}%`
          const tagName = selectedTag?.name
          const isSelected = selectedBBox && selectedBBoxIndex === index
          const displayText = (() => {
            if (!isSelected) return confidenceText
            if (!tagName) return confidenceText
            return labelMode === 'class' ? tagName : `${tagName} ${confidenceText}`
          })()

          // Setup text style
          ctx.font = `${12 / (zoomLevel * cssScale)}px Arial`
          ctx.textBaseline = 'top'

          // Create background for better readability (unchanged size)
          const textMetrics = ctx.measureText(displayText)
          const padding = 4 / (zoomLevel * cssScale)
          const textHeight = 14 / (zoomLevel * cssScale)

          ctx.fillStyle = color
          ctx.fillRect(
            (labelBox.xmin ?? 0) * bBoxScaleX - 1 / (zoomLevel * cssScale),
            (labelBox.ymin ?? 0) * bBoxScaleY - textHeight - padding,
            textMetrics.width + padding * 2,
            textHeight + padding
          )

          // Draw text (unchanged position)
          ctx.fillStyle = getContrastYIQ(color)
          ctx.fillText(
            displayText,
            (labelBox.xmin ?? 0) * bBoxScaleX + padding / 2,
            (labelBox.ymin ?? 0) * bBoxScaleY - textHeight - padding / 2
          )

          // Draw status icon to the LEFT of the label container if selected
          const status =
            bboxStatuses && box.class_index !== undefined
              ? bboxStatuses[box.class_index]
              : undefined
          if (status) {
            const iconSize = 14 / (zoomLevel * cssScale)
            const iconGap = 4 / (zoomLevel * cssScale)
            // Position icon to the left of the label background
            const iconX =
              (labelBox.xmin ?? 0) * bBoxScaleX -
              iconSize / 2 -
              iconGap -
              1 / (zoomLevel * cssScale)
            const iconY = (labelBox.ymin ?? 0) * bBoxScaleY - textHeight / 2 - padding / 2
            drawStatusIcon(ctx, iconX, iconY, status, iconSize)
          }
        }

        // AAL Review: Draw status icon overlay (eye/checkmark/X) in center
        // Only show center icon if box is NOT selected (selected boxes show icon next to label)
        if (bboxStatuses && box.class_index !== undefined) {
          const status = bboxStatuses[box.class_index]
          const isBoxSelected = selectedBBox && selectedBBoxIndex === index

          if (status && !isBoxSelected) {
            // Convert box to natural space for center icon positioning
            const needsScaling =
              contentWidth && contentHeight && (contentWidth !== imgW || contentHeight !== imgH)

            let centerIconBox = box
            if (needsScaling && contentWidth && contentHeight) {
              const scaleX = imgW / contentWidth
              const scaleY = imgH / contentHeight

              if (box.points && box.points.length === 8) {
                centerIconBox = {
                  ...box,
                  points: box.points.map((coord, idx) =>
                    idx % 2 === 0 ? coord * scaleX : coord * scaleY
                  ),
                  xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
                  ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
                  xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
                  ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
                }
              } else {
                centerIconBox = {
                  ...box,
                  xmin: box.xmin !== undefined ? box.xmin * scaleX : undefined,
                  ymin: box.ymin !== undefined ? box.ymin * scaleY : undefined,
                  xmax: box.xmax !== undefined ? box.xmax * scaleX : undefined,
                  ymax: box.ymax !== undefined ? box.ymax * scaleY : undefined,
                }
              }
            }

            // Calculate box center for icon placement
            const pts =
              centerIconBox.points && centerIconBox.points.length === 8
                ? centerIconBox.points
                : [
                    centerIconBox.xmin ?? 0,
                    centerIconBox.ymin ?? 0,
                    centerIconBox.xmax ?? 0,
                    centerIconBox.ymin ?? 0,
                    centerIconBox.xmax ?? 0,
                    centerIconBox.ymax ?? 0,
                    centerIconBox.xmin ?? 0,
                    centerIconBox.ymax ?? 0,
                  ]
            const xs = [pts[0], pts[2], pts[4], pts[6]]
            const ys = [pts[1], pts[3], pts[5], pts[7]]
            const centerXImg = (Math.min(...xs) + Math.max(...xs)) / 2
            const centerYImg = (Math.min(...ys) + Math.max(...ys)) / 2
            const centerX = centerXImg * bBoxScaleX
            const centerY = centerYImg * bBoxScaleY

            // Icon size based on actual rendered box size on screen
            const boxWidthCanvas = (Math.max(...xs) - Math.min(...xs)) * bBoxScaleX
            const boxHeightCanvas = (Math.max(...ys) - Math.min(...ys)) * bBoxScaleY
            const boxMinDimCanvas = Math.min(boxWidthCanvas, boxHeightCanvas)
            // Icon is 8% of box size, with reasonable min/max bounds
            const iconSize = Math.max(8, Math.min(28, boxMinDimCanvas * 0.08))

            drawStatusIcon(ctx, centerX, centerY, status, iconSize)
          }
        }
      }
    })

    // Draw new bounding box if it exists
    if (newBoundingBox) {
      ctx.strokeStyle = 'blue'
      ctx.lineWidth = 2 / (zoomLevel * cssScale)
      if (newBoundingBox.points && newBoundingBox.points.length === 8) {
        // Use oriented drawing for preview when points are present
        drawBoundingBoxFromPoints(
          ctx,
          newBoundingBox.points,
          imgW,
          imgH,
          drawW,
          drawH,
          'blue',
          undefined,
          2 / (zoomLevel * cssScale)
        )
      } else {
        // Fallback to axis-aligned rect
        ctx.strokeRect(
          Math.round((newBoundingBox.xmin ?? 0) * bBoxScaleX),
          Math.round((newBoundingBox.ymin ?? 0) * bBoxScaleY),
          Math.round(((newBoundingBox.xmax ?? 0) - (newBoundingBox.xmin ?? 0)) * bBoxScaleX),
          Math.round(((newBoundingBox.ymax ?? 0) - (newBoundingBox.ymin ?? 0)) * bBoxScaleY)
        )
      }
    }

    // If in OBB axisStart phase, draw a dotted alignment line from axisStart to mouse
    if (creationAxisStart && creationPhase === 'axisStart') {
      // Convert image-space start to pre-transform canvas coordinates
      const sx = creationAxisStart.x * bBoxScaleX
      const sy = creationAxisStart.y * bBoxScaleY
      // Convert mouse (canvas pixel) to pre-transform world coordinates
      const ex =
        (mousePosition.x - canvas.width / 2) / zoomLevel + canvas.width / 2 - panOffset.x - offsetX
      const ey =
        (mousePosition.y - canvas.height / 2) / zoomLevel +
        canvas.height / 2 -
        panOffset.y -
        offsetY
      ctx.save()
      ctx.setLineDash([4 / (zoomLevel * cssScale), 4 / (zoomLevel * cssScale)])
      ctx.strokeStyle = 'rgba(0, 123, 255, 0.9)'
      ctx.lineWidth = 1.5 / (zoomLevel * cssScale)
      ctx.beginPath()
      ctx.moveTo(sx, sy)
      ctx.lineTo(ex, ey)
      ctx.stroke()
      ctx.restore()
    }

    // Restore translation for image space
    ctx.restore()
  }

  // Always restore the context state
  ctx.restore()

  // Draw crosshairs at mouse position in screen space (after restoring transformations)
  // Always show crosshairs when useBBoxes is true (not just when mouse is over canvas)
  if (useBBoxes) {
    const canvasX = mousePosition?.x ?? canvas.width / 2
    const canvasY = mousePosition?.y ?? canvas.height / 2

    ctx.beginPath()
    ctx.strokeStyle = 'rgba(0, 123, 255, 0.5)'
    ctx.lineWidth = 1

    // Vertical line
    ctx.moveTo(canvasX, 0)
    ctx.lineTo(canvasX, canvas.height)

    // Horizontal line
    ctx.moveTo(0, canvasY)
    ctx.lineTo(canvas.width, canvasY)

    ctx.stroke()
  }
}
